<template>
  <div class="policy-finalization">
    <PolicyData
      class="policy-finalization__main-policy"
      :petType="pet.petType"
      :petBreed="pet.petBreed || ''"
      :petSex="pet.petSex"
      :petAlias="pet.petAlias || ''"
      :petBirthDate="formatDate(pet.petBirthDate) || ''"
      :price="getTotalBasePrice"
      :fullName="getPolicyholderFullname"
      :birthdate="formatDate(policyholder.dateOfBirth) || ''"
      :email="policyholder.email || ''"
      :phone="policyholder.phone || ''"
      :taxNumber="policyholder.taxNumber || ''"
      :city="getAddressCity"
      :address="getAddressFull"
      :documentNumber="getDocumentDisplayNumber"
      :documentIssuedBy="document.issuingAuthority || ''"
      :documentDate="formatDate(document.issueDate) || ''"
      :period="getPeriodLabel"
      :startDate="formatDate(contract.startDate) || ''"
      :endDate="getContractEndDate(contract.startDate, contract.estimatedPeriod) || ''"
      :isStartDateDisabled="isOrderCreated"
      @dateChange="onStartDateChange"
    />

    <PriceCard
      class="policy-finalization__pricing-summary"
      :finishPrice="getTotalDiscountedPrice"
      :oldPrice="0"
      :discountAmount="0"
      :earnings="getEarnings"
      :isPriceLoading="isPriceLoading"
      @bonusApplied="onBonusApplied"
    />

    <div class="policy-finalization__controls">
      <AccentButton
        class="policy-finalization__edit-button"
        :disabled="isOrderCreated"
        @click="onEditData"
      >
        Редагувати дані
      </AccentButton>
      <BlueOutlineButton
        v-if="orderId && isOrderCreated"
        class="policy-finalization__draft-button"
        @click="openDraft"
      >
        Переглянути чернетку
      </BlueOutlineButton>
    </div>

    <div
      v-if="isSanctioned"
      class="policy-finalization__sanctions-warning"
    >
      <p class="policy-finalization__sanctions-message">
        {{ getSanctionStatusMessage }}
      </p>
    </div>

    <div class="policy-finalization__actions">
      <Checkbox
        v-model="isAgreementAccepted"
        :is-disabled="isOrderCreated"
        class="policy-finalization__agreement"
        label="Згоден з обробкою персональних даних. Дані будуть передані в страхову компанію для формування поліса."
      />

      <BlueButton
        v-if="!isOrderCreated"
        class="policy-finalization__btn-order"
        :is-disabled="isPurchaseDisabled"
        :is-loading="isSubmitting"
        @click="onSubmitOrder"
      >
        Заявити
      </BlueButton>
      <BlueButton
        v-if="canShowPayButton"
        class="policy-finalization__btn-order"
        @click="onPayPolicy"
      >
        Сплатити поліс
      </BlueButton>

      <PaymentStatusPolling
        v-if="isOrderCreated"
        class="policy-finalization__payment-status-polling"
        ref="paymentPolling"
        :order-id="orderId"
        :payments-data="getPayments"
        :payment-invoice-id="getPaymentInvoiceId"
        :is-paid="getIsPaid"
        :payment-check-result="paymentCheckResult"
        @fetch-payments="onFetchPaymentsRequest"
        @check-payment="onCheckPaymentRequest"
        @set-payment-invoice-id="onSetPaymentInvoiceIdRequest"
        @set-payment-system-code="onSetPaymentSystemCodeRequest"
        @processed="onPaymentProcessed"
      />
    </div>

    <OtpSection
      v-if="isOrderCreated && requiresOtp"
      :order-id="orderId"
      :requires-otp="requiresOtp"
      :otp-flags="otpFlags"
      :is-otp-sending="otpSendingInProgress"
      :otp-sending-error="otpSendingError"
      :is-otp-checking="otpCheckingInProgress"
      :otp-checking-error="otpCheckingError"
      :otp-additional-codes="otpAdditionalCodes"
      @send-otp="onSendOtpRequest"
      @check-otp="onCheckOtpRequest"
      @clear-otp-check-error="onClearOtpCheckError"
      @verified="onOtpVerified"
    />

    <ReceiptUpload
      v-if="showReceiptUpload"
      :payments="getPayments"
      @uploaded="onReceiptUploaded"
      @removed="onReceiptRemoved"
    />

    <AccentButton
      v-if="getIsPaid || receiptLoaded"
      class="policy-finalization__btn-order"
      :is-disabled="isConfirmDisabled"
      @click="onConfirmContract"
    >
      Оформити
    </AccentButton>

    <ContractProcessingPopup
      ref="contractProcessingPopup"
    />

    <PolicySuccessPopup
      ref="policySuccessPopup"
      @closed="onPolicySuccessClosed"
    />

    <SanctionsAlertModal
      :sanctions-data="getSanctions"
      :is-sanctioned="isSanctioned"
      @set-sanctions="onCloseSanctions"
    />

    <DeclaredSuccessPopup
      ref="declaredSuccessPopup"
      @closed="onDeclaredSuccessClosed"
    />

    <BlockedOrdersPopup
      ref="blockedOrdersPopup"
    />

    <BlacklistPopup
      ref="blacklistPopup"
    />

    <ResponseStatusPopup
      ref="responseStatusPopup"
      @edit-data="onEditData"
      @search-offers="onSearchOffers"
    />

    <PaymentSystemSelector
      ref="paymentSystemSelector"
      :company-id="getCompanyId"
      :program-name="getProgramName"
      @selectPaymentSystem="onSelectPaymentSystem"
      @selectLegalInvoice="onSelectLegalInvoice"
    />

    <PaymentLinkPopup
      ref="paymentLinkPopup"
      :order-id="orderId"
      :price="getTotalDiscountedPrice"
      :provider-data="selectedProviderData"
      @invoice-created="onInvoiceCreated"
      @closed="onPaymentLinkClosed"
    />
  </div>
</template>

<script>
import moment from 'moment';
import { mapActions, mapGetters } from 'vuex';
import { default as PolicyData } from './policy-data';
import { default as ResponseStatusPopup } from './response-status-popup';
import {
  petsPeriodLabels,
  sanctionsFallbackMessage,
  sanctionsStatusMessage,
} from '@/entities/insurance';
import {
  confirmContractResults,
  invoiceTypes,
  orderStatuses,
  otpResults,
  saveOrderResults,
} from '@/entities/insurance/config';
import { PetsOrderParamsMapper } from '@/entities/insurance/mapper/pets-order-params';
import { mapPetsOfferParams } from '@/entities/insurance/mapper';
import { FRANCHISE_DAYS } from '@/features/pets-calculator/config';
import { DateFormatter } from '@/shared/lib/calendar';
import { Download } from '@/shared/lib/download';
import { PolicyholderMapper } from '@/shared/lib/insurance';
import {
  ContractLogic,
  ContractState,
  DocumentState,
  OfferState,
  OrderState,
  PetsPolicyholderState,
  PetState,
  PolicyholderAddress,
  PolicyholderLogic,
} from '@/shared/lib/insurance';
import { AccentButton, BlueButton, BlueOutlineButton, Checkbox } from '@/shared/ui/form';
import {
  BlacklistPopup,
  BlockedOrdersPopup,
  ContractProcessingPopup,
  DeclaredSuccessPopup,
  OtpSection,
  PaymentLinkPopup,
  PaymentStatusPolling,
  PaymentSystemSelector,
  PolicySuccessPopup,
  PriceCard,
  ReceiptUpload,
  SanctionsAlertModal,
} from '@/shared/ui/insurance';

const { formatDate } = DateFormatter();
const policyholderMapper = PolicyholderMapper();
const { preparePetsOrderDraft } = PetsOrderParamsMapper();

export default {
  data() {
    return {
      appliedBonuses: 0,
      isPriceLoading: false,
      isAgreementAccepted: false,
      isOrderCreated: false,
      isSubmitting: false,
      selectedProviderData: null,
      receiptLoaded: false,
      otpSendingInProgress: false,
      otpSendingError: false,
      otpCheckingInProgress: false,
      otpCheckingError: false,
      otpAdditionalCodes: null,
      paymentCheckResult: null,
    };
  },
  mounted() {
    this.isOrderCreated = this.orderStatusId === orderStatuses.DECLARED;

    const minStartDate = moment().add(FRANCHISE_DAYS, 'days').startOf('day');
    if (!this.contract.startDate || moment(this.contract.startDate, 'DD.MM.YYYY').isBefore(minStartDate)) {
      this.contract.startDate = minStartDate.format('DD.MM.YYYY');
    }

    this.actualizePrice();
    this.loadPolicyholderData();

    if (this.orderId) {
      this.fetchOrderPayments();
    }
  },
  components: {
    PolicyData, PriceCard, AccentButton, Checkbox,
    BlueButton, BlueOutlineButton, ContractProcessingPopup, PolicySuccessPopup, SanctionsAlertModal, DeclaredSuccessPopup,
    BlockedOrdersPopup, BlacklistPopup, ResponseStatusPopup, ReceiptUpload, PaymentSystemSelector,
    PaymentLinkPopup, OtpSection, PaymentStatusPolling,
  },
  mixins: [
    PetState('petsModule'), PetsPolicyholderState('petsModule'), OrderState('petsModule'),
    OfferState('petsModule'), DocumentState('petsModule'), ContractState('petsModule'),
    PolicyholderAddress, PolicyholderLogic, ContractLogic,
  ],
  computed: {
    ...mapGetters('accountBalanceModule', ['getBalanceAmount']),
    ...mapGetters('petsModule', ['getMtsbuCode', 'getMtsbuLink', 'getIsPaid', 'getPaymentInvoiceId', 'getPaymentSystemCode']),
    ...mapGetters('petsOffersModule', ['getOffersList']),
    ...mapGetters('currentUserModule', ['getUserInfo', 'getCurrentUserId', 'getParentId']),
    isFromHistory() {
      return this.$route.query.from === 'history';
    },
    getCompanyId() {
      return this.getOffer?.company?.id || null;
    },
    getProgramName() {
      return this.getOffer?.programmName || null;
    },
    getCompanyName() {
      return this.getOffer?.company?.name || '';
    },
    getIdentificationCode() {
      return this.policyholder.taxNumber || '';
    },
    getUserId() {
      return this.getParentId ?? this.getCurrentUserId;
    },
    getTotalBasePrice() {
      return Number(this.getOffer?.pricing?.basePrice) || 0;
    },
    getTotalDiscountedPrice() {
      return Number(this.getOffer?.pricing?.finalPrice) || 0;
    },
    getEarnings() {
      return Number(this.getOffer?.pricing?.agentCommission?.own) || 0;
    },
    getDocumentDisplayNumber() {
      const { serialNumber, documentNumber } = this.document;
      if (serialNumber && documentNumber) return `${serialNumber} ${documentNumber}`;
      return documentNumber || serialNumber || '';
    },
    getPolicyholderFullname() {
      return this.policyholder.fullName?.fullNameUa || '';
    },
    getAddressCity() {
      return this.policyholder.address?.city?.nameFullNameUa || '';
    },
    getAddressFull() {
      const address = this.policyholder.address;
      if (!address) return '';
      return [
        address.city?.nameFullNameUa,
        address.street?.nameUa ? `вул. ${address.street.nameUa}` : null,
        address.buildingNumber,
        address.apartmentNumber ? `кв. ${address.apartmentNumber}` : null,
      ].filter(Boolean).join(', ');
    },
    getPeriodLabel() {
      return petsPeriodLabels[this.contract.estimatedPeriod] || `${this.contract.estimatedPeriod} міс.`;
    },
    isPurchaseDisabled() {
      return this.isSanctioned || this.isSubmitting || !this.isAgreementAccepted;
    },
    isConfirmDisabled() {
      if (this.requiresOtp && !this.otpFlags.otpVerified) return true;
      if (!this.getIsPaid && !this.receiptLoaded) return true;

      return false;
    },
    canShowPayButton() {
      if (!this.isOrderCreated) return false;
      if (this.getIsPaid) return false;
      if (this.requiresOtp && !this.otpFlags?.otpVerified) return false;

      return true;
    },
    showReceiptUpload() {
      if (this.getUserInfo?.organizationId) return false;
      if (!this.isOrderCreated) return false;
      if (this.requiresOtp && !this.otpFlags?.otpVerified) return false;

      return this.getPayments?.some(p => p.invoice?.type_id === invoiceTypes.LEGAL_ENTITY_INSURANCE_PAYMENT);
    },
    getSanctionStatusMessage() {
      const status = this.getSanctions?.status;
      if (status && sanctionsStatusMessage[status]) return sanctionsStatusMessage[status];
      if (status === saveOrderResults.SANCTIONED || status === saveOrderResults.POSSIBLE_MATCH) return sanctionsStatusMessage.possible_match;
      return sanctionsFallbackMessage;
    },
  },
  methods: {
    ...mapActions('notificationModule', ['showNotificationErrorMessage']),
    ...mapActions('policyholderModule', ['fetchPolicyholder']),
    ...mapActions('petsModule', ['setOffer', 'confirmContract', 'fetchOrderPayments', 'setPaymentInvoiceId', 'setPaymentSystemCode', 'sendOtp', 'checkOtp', 'checkPaymentStatus']),
    ...mapActions('petsOffersModule', ['calculateOffers']),
    ...mapActions('paymentLinkModule', ['createPaymentLink']),
    formatDate,
    async actualizePrice() {
      if (!this.getOffer?.module?.id) return;
      if (!this.contract?.startDate || !this.contract?.estimatedPeriod) return;

      const earnings = this.getOffer?.earningsParam;
      if (!earnings) return;

      this.isPriceLoading = true;
      try {
        await this.calculateOffers({
          startFrom: this.contract.startDate,
          insurancePeriod: this.contract.estimatedPeriod,
          earnings,
        });

        const offersList = this.getOffersList || [];
        const updatedOffer = offersList.find(o =>
          o.id === this.getOffer.id ||
          (o.module?.id === this.getOffer.module?.id && o.company?.id === this.getOffer.company?.id)
        );

        if (updatedOffer) {
          this.setOffer(updatedOffer);
        }
      } catch (_) {
        // non-critical — silence failures so the page still loads
      } finally {
        this.isPriceLoading = false;
      }
    },
    onSelectPaymentSystem({ providerData }) {
      this.selectedProviderData = providerData;
      this.setPaymentSystemCode(providerData?.code || null);
      this.$nextTick(() => {
        this.$refs.paymentLinkPopup.open(this.orderId, this.getTotalDiscountedPrice, providerData);
      });
      this.$refs.paymentPolling?.startPolling();
    },
    async onSelectLegalInvoice() {
      const loading = this.$loading({
        lock: true,
        text: 'Формування рахунку-фактури...',
        spinner: 'el-icon-loading',
        background: 'rgba(0, 0, 0, 0.7)',
      });

      try {
        if (!this.getUserInfo?.organizationId) {
          await this.createPaymentLink({
            orderId: this.orderId,
            amount: this.getTotalDiscountedPrice,
            invoiceType: 'legal-entity-insurance',
          });
        }

        await this.fetchOrderPayments(this.orderId);
        this.persistPaymentInvoiceId();
        this.setPaymentSystemCode('legal-entity');

        const mtsbuCode = this.getMtsbuCode;
        const invoiceLegalData = {
          company: this.getCompanyName,
          code: this.getIdentificationCode,
          price: this.getTotalDiscountedPrice,
          mtsbuCode: mtsbuCode,
        };

        const params = new URLSearchParams(invoiceLegalData).toString();
        const url = `/api/orders/show-invoice-legal/${this.getUserId}/${this.orderId}?${params}`;
        window.open(url, '_blank');

        this.$refs.paymentPolling?.startPolling();
      } catch (e) {
        console.error('Error creating legal invoice', e);
        this.$toast?.error?.('Помилка при формуванні рахунку-фактури');
      } finally {
        loading.close();
      }
    },
    onPaymentProcessed() {
      this.$refs.paymentLinkPopup?.close();
    },
    onPaymentLinkClosed() {
      this.selectedProviderData = null;
    },
    async onInvoiceCreated() {
      await this.fetchOrderPayments(this.orderId);
      this.persistPaymentInvoiceId();
    },
    persistPaymentInvoiceId() {
      const invoice = this.getPayments?.find(p => p.invoice?.type_id === invoiceTypes.INSURANCE_PAYMENT);
      if (invoice?.invoice?.id) {
        this.setPaymentInvoiceId(invoice.invoice.id);
      }
    },
    onPayPolicy() {
      this.$refs.paymentSystemSelector.open({
        price: this.getTotalDiscountedPrice,
        earnings: this.getEarnings,
        orderData: { orderId: this.orderId },
      });
    },
    onBonusApplied(appliedBonuses) {
      this.appliedBonuses = appliedBonuses;
    },
    async onConfirmContract() {
      const data = {
        orderId: this.orderId,
        bonusSystemValue: this.appliedBonuses,
      };

      this.$refs.contractProcessingPopup?.open();

      const result = await this.confirmContract(data);

      this.$refs.contractProcessingPopup?.close();

      if (result?.error === confirmContractResults.INSUFFICIENT_FUNDS) {
        this.$refs.responseStatusPopup?.openConfirmError(result.message);
        return;
      }

      if (result?.error === confirmContractResults.RESPONSE_MESSAGE) {
        this.$refs.responseStatusPopup?.openConfirmError(result.message);
        return;
      }

      if (result?.error === confirmContractResults.UNKNOWN) {
        this.$refs.responseStatusPopup?.openConfirmError('Сталася невідома помилка');
        return;
      }

      if (result?.status === 'success') {
        const policyLink = result?.policyLink || result?.data?.[0]?.policyLink || this.getMtsbuLink || '';
        const policyCode = result?.data?.[0]?.agreementNum || '';
        const companyLogo = this.getOffer?.company?.logo || '';
        const companyName = this.getCompanyName;
        this.$refs.policySuccessPopup?.open(policyLink, companyLogo, companyName, policyCode);
        return;
      }

      this.$refs.responseStatusPopup?.openConfirmError('Сталася невідома помилка');
    },
    onSaveOrderError(result) {
      const ERROR_HANDLERS = {
        [saveOrderResults.BLOCKED]: () => this.$refs.blockedOrdersPopup?.open(),
        [saveOrderResults.BLACKLIST]: () => this.$refs.blacklistPopup?.open(),
        [saveOrderResults.RESPONSE_MESSAGE]: () => this.$refs.responseStatusPopup?.openSubmitError(result.message, result.errorInfo),
        [saveOrderResults.ERROR_MESSAGE]: () => this.showNotificationErrorMessage({ title: 'Помилка', message: result.message }),
      };

      const handler = ERROR_HANDLERS[result.result];
      if (handler) {
        handler();
        return true;
      }

      if (result.result === saveOrderResults.SANCTIONED) {
        return true;
      }

      if (result.result === saveOrderResults.SUCCESS || result.result === saveOrderResults.DECLARED_SUCCESS) {
        return false;
      }

      this.showNotificationErrorMessage({ title: 'Помилка', message: 'Страхування тварин не заявлено, перевірте будь ласка дані і спробуйте ще!' });

      return true;
    },
    async onSubmitOrder() {
      this.isSubmitting = true;
      try {
        const orderDraft = preparePetsOrderDraft({
          petType: this.pet.petType,
          petBreed: this.pet.petBreed,
          petSex: this.pet.petSex,
          petAlias: this.pet.petAlias,
          petBirthDate: this.pet.petBirthDate,
          vetPassportSerial: this.$store.state.petsModule.vetPassport.serial,
          vetPassportNumber: this.$store.state.petsModule.vetPassport.number,
          vetPassportDate: this.$store.state.petsModule.vetPassport.date,
          vetPassportIssuedBy: this.$store.state.petsModule.vetPassport.issuedBy,
          microchipCode: this.$store.state.petsModule.microchip.code,
          microchipDate: this.$store.state.petsModule.microchip.date,
          insurancePeriod: this.contract.estimatedPeriod,
          earnings: this.$store.state.petsModule.calculator.earnings,
          startDate: this.contract.startDate,
          policyholder: this.policyholder,
          document: {
            ...this.document,
            type: this.document.documentType,
            number: this.document.documentNumber,
          },
          offerInfo: mapPetsOfferParams(this.getOffer),
          orderId: this.orderId,
          statusId: orderStatuses.DECLARED,
        });

        const submitResult = await this.saveOrder(orderDraft);

        if (submitResult.result === saveOrderResults.DECLARED_SUCCESS) {
          this.$refs.declaredSuccessPopup?.open(submitResult.mtsbuLink, submitResult.warningCancelMessage);
          this.isOrderCreated = true;
          this.orderStatusId = orderStatuses.DECLARED;
          if (submitResult.orderId) this.orderId = submitResult.orderId;
          this.fetchOrderPayments();
        } else if (submitResult.result === saveOrderResults.SUCCESS) {
          this.isOrderCreated = true;
          this.orderStatusId = orderStatuses.DECLARED;
          if (submitResult.orderId) this.orderId = submitResult.orderId;
          this.fetchOrderPayments();
        } else {
          this.onSaveOrderError(submitResult);
        }
      } finally {
        this.isSubmitting = false;
      }
    },
    onStartDateChange(dateValue) {
      const dateFromMax = this.getOffer?.dateFromMax || 90;
      const maxDate = moment().add(dateFromMax, 'days');
      const selected = moment(dateValue);
      if (selected.isAfter(maxDate, 'day')) {
        this.contract.startDate = maxDate.format('DD.MM.YYYY');
        return;
      }
      this.contract.startDate = moment(dateValue).format('DD.MM.YYYY');
    },
    onPolicySuccessClosed() {
      this.$router.push({ name: 'history' });
    },
    onEditData() {
      this.$router.push({ name: 'petsPolicyDetails' });
    },
    onSearchOffers() {
      this.$router.push({ name: 'petsCalculator' });
    },
    openDraft() {
      const downloader = Download();
      const basePath = `/orders/draft/${this.getUserId}/${this.orderId}`;
      downloader.download(basePath, 'draft.pdf');
    },
    async onDeclaredSuccessClosed() {
      this.isOrderCreated = true;
      this.orderStatusId = orderStatuses.DECLARED;
    },
    async loadPolicyholderData() {
      const policyholderNeedsLoad = policyholderMapper.needsPolicyholderLoad(this.policyholder);
      const documentNeedsLoad = policyholderMapper.needsDocumentLoad(this.document);

      if (!policyholderNeedsLoad && !documentNeedsLoad) return;
      if (!this.policyholder?.id) return;

      try {
        const response = await this.fetchPolicyholder(this.policyholder.id);

        if (response && !Array.isArray(response)) {
          if (policyholderNeedsLoad) {
            this.policyholder = policyholderMapper.mapPolicyholderData(this.policyholder, response, this.convertToAddress, this.formatDate);
          }

          if (documentNeedsLoad) {
            const responseDocument = response?.documents?.find(doc => doc.id === this.document.id);
            if (responseDocument) {
              this.document = policyholderMapper.mapDocumentData(this.document, responseDocument, this.formatDate);
            }
          }
        }
      } catch {
        // intentionally empty - policyholder data load failure is non-critical
      }
    },
    onOtpVerified() {
      this.setOtpFlags({ ...this.otpFlags, otpVerified: true, otpSent: true });
    },
    onOtpSent() {
      this.setOtpFlags({ ...this.otpFlags, otpSent: true });
    },
    async onSendOtpRequest({ type, commissionPaidBy }) {
      this.otpSendingInProgress = true;
      this.otpSendingError = false;
      this.otpAdditionalCodes = null;
      const result = await this.sendOtp({ type, commissionPaidBy });
      this.otpSendingInProgress = false;
      if (result.error) {
        this.otpSendingError = true;
      } else {
        this.onOtpSent();
        if (result.additionalOtps) {
          this.otpAdditionalCodes = result.additionalOtps;
        }
      }
    },
    async onCheckOtpRequest({ otp, additionalOtps }) {
      this.otpCheckingInProgress = true;
      this.otpCheckingError = false;
      const result = await this.checkOtp({ otp, additionalOtps });
      this.otpCheckingInProgress = false;
      if (result.error === otpResults.INVALID) {
        this.otpCheckingError = true;
      }
    },
    onClearOtpCheckError() {
      this.otpCheckingError = false;
    },
    onFetchPaymentsRequest(orderId) {
      this.fetchOrderPayments(orderId);
    },
    async onCheckPaymentRequest(invoiceId) {
      try {
        const result = await this.checkPaymentStatus(invoiceId);
        this.paymentCheckResult = { ...result, invoiceId };
      } catch (e) {
        this.paymentCheckResult = { error: e.message || 'Помилка перевірки оплати', invoiceId };
      }
    },
    onSetPaymentInvoiceIdRequest(value) {
      this.setPaymentInvoiceId(value);
    },
    onSetPaymentSystemCodeRequest(value) {
      this.setPaymentSystemCode(value);
    },
    onCloseSanctions(data) {
      this.setSanctions(data);
    },
    onReceiptUploaded() {
      this.receiptLoaded = true;
    },
    onReceiptRemoved() {
      this.receiptLoaded = false;
    },
  },
  watch: {
    getPolicyholderId(newId) {
      if (newId) {
        this.loadPolicyholderData();
      }
    },
  },
};
</script>

<style scoped lang="scss">
.policy-finalization {
  &__main-policy {
    margin-bottom: 16px;
  }

  &__pricing-summary {
    margin-bottom: 16px;
  }

  &__controls {
    display: flex;
    gap: 16px;

    @media (max-width: 768px) {
      flex-direction: column;
    }
  }

  &__edit-button {
    padding: 15px 85px;
    margin-bottom: 24px;
    width: 350px;

    @media (max-width: 768px) {
      width: 100%;
    }
  }

  &__draft-button {
    padding: 15px 85px;
    margin-bottom: 24px;
    width: 350px;

    @media (max-width: 768px) {
      width: 100%;
    }
  }

  &__sanctions-warning {
    background-color: #FEF2F2;
    border: 1px solid #FCA5A5;
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 24px;
  }

  &__sanctions-message {
    font-size: 14px;
    font-weight: 500;
    color: #DC2626;
    margin: 0;
    line-height: 1.5;
  }

  &__agreement {
    max-width: 50%;
    margin-bottom: 16px;

    @media (max-width: 768px) {
      max-width: 100%;
    }
  }

  &__payment-status-polling {
    width: 350px;
    margin-bottom: 16px;

    @media (max-width: 768px) {
      width: 100%;
    }
  }

  &__actions {
    display: flex;
    flex-direction: column;
  }

  &__btn-order {
    padding: 15px 150px;
    margin-top: 16px;
    width: 350px;

    @media (max-width: 768px) {
      width: 100%;
      padding: 15px 24px;
    }
  }
}

::v-deep .policy-finalization__agreement .checkbox__text {
  font-size: 12px;
  color: #717680;
}
</style>
